import dayjs, {Dayjs} from 'dayjs';
import {fromJson, json, JsonMappers} from '../core/data-source/json.mapper';
import {getStrings} from '../core/lang';
import currenciesSignStrings from '../lang/currencies-sign.strings.yaml';
import {PriorityRecord} from './priority.record';
import {RoutingModeRecord} from './routing-mode.record';
import {StopSequenceRecord} from './stop-sequence.record';
import {httpService} from '../core/services/http.service';
import {KpiScheme} from '../components/planning-tool/components/schedule/components/scene-kpi/kpi-scheme.interface';

function eulaStepsParser(value: string): any[] {
    if (!value || !value.length || value.indexOf('{') == -1) return [];
    try {
        return JSON.parse(value);
    } catch (e) {
        return [];
    }
}

function getFloat(rawValue: string): number {
    let value: number = parseFloat(rawValue);
    if (isNaN(value)) return 0;
    return value;
}
export class SettingsRecord {
    lang: any = getStrings(currenciesSignStrings);

    @json(JsonMappers.ArrayOf(() => PriorityRecord))
    priorities: PriorityRecord[];

    @json(JsonMappers.ArrayOf(() => StopSequenceRecord))
    stopSequence: StopSequenceRecord[];

    @json({
        jsonName: 'ACCOUNT_LOGO',
        mapper: {from: (value: string): string => (value ? httpService.getBaseUrl() + value : void 0)}
    })
    accountLogo: string;

    @json(JsonMappers.Boolean)
    enableAPIv6KeyManagementScreen: boolean;

    @json({
        jsonName: 'export_file_format'
    })
    exportFileFormat: string; // CSV, XLS, XLT

    @json()
    availableLanguages: string[];

    @json(JsonMappers.Boolean)
    showReleaseNotesForDispatcher: boolean;
    @json(JsonMappers.Boolean)
    enableSpeedZonesTab: boolean;
    @json(JsonMappers.Boolean)
    enableSchedulingZonesTab: boolean;
    @json(JsonMappers.Boolean)
    orderHasCost: boolean;
    @json(JsonMappers.Boolean)
    multiDropWindowsEnabled: boolean;
    @json(JsonMappers.Boolean)
    dropDurationRequired: boolean;
    @json(JsonMappers.Boolean)
    enableCapacityConstraint: boolean;
    @json(JsonMappers.Boolean)
    enableVolumeConstraint: boolean;
    @json(JsonMappers.Boolean)
    dropDurationGreaterThan1: boolean;
    @json()
    dateFormats: any;
    @json(JsonMappers.Boolean)
    useNewManifest: boolean;
    @json(JsonMappers.Boolean)
    dispatcherCanEditSettings: boolean;
    @json(JsonMappers.Boolean)
    isWhiteLabelBuild: boolean;
    @json(JsonMappers.Boolean)
    enableOrderItemsTab: boolean;

    @json(JsonMappers.Boolean)
    appUsers: boolean;

    @json(JsonMappers.Boolean)
    enablePickupAndDelivery: boolean;

    @json({
        jsonName: 'ct.enableHistoricalTraffic',
        mapper: JsonMappers.Boolean
    })
    enableHistoricalTraffic: boolean;

    @json({
        jsonName: 'ct.markerColorScheme',
        mapper: {from: (value: string): any => new Function(`return ${value}`)()}
    })
    markerColorScheme: any;

    @json({
        jsonName: 'ct.useClusters',
        mapper: JsonMappers.Boolean,
        value: false
    })
    useClusters: boolean;

    @json({
        jsonName: 'ct.addPostcodeToOrderName',
        mapper: JsonMappers.Boolean
    })
    addPostcodeToOrderName: boolean;

    @json({
        jsonName: 'ct.kpiScheme',
        mapper: {
            from: (value: string): any => {
                const parsed = new Function(`return${value}`)();
                Object.keys(parsed).forEach((key) => {
                    if (typeof parsed[key].visible === 'string') {
                        parsed[key].visible = parsed[key].visible === 'true';
                    }
                });
                return parsed;
            }
        }
    })
    kpiScheme: KpiScheme;

    @json(JsonMappers.Boolean)
    onboardingModeOn: boolean;
    @json(JsonMappers.Boolean)
    selfImplementationWizard: boolean;
    @json(JsonMappers.Boolean)
    isEmailConfiguration: boolean; // используется email server maxoptra

    //reports
    @json(JsonMappers.Boolean)
    enableReportsTab: boolean;
    @json(JsonMappers.Boolean)
    enableConsolidatedReportTab: boolean;
    @json(JsonMappers.Boolean)
    enablePerformerPerformanceReportTab: boolean;
    @json(JsonMappers.Boolean)
    enableWarehouseInstructionsReportTab: boolean;
    @json(JsonMappers.Boolean)
    enableOnTimeReportTab: boolean;
    @json(JsonMappers.Boolean)
    enableOverallDeliveryAccuracyReportTab: boolean;
    @json(JsonMappers.Boolean)
    enableDailyRunsReportTab: boolean;
    @json(JsonMappers.Boolean)
    enableLoadingResourcesReportTab: boolean;
    @json(JsonMappers.Boolean)
    loaderReport: boolean;

    // EULA
    // @json({jsonName: 'maxoptra.en.eula', mapper: {from: eulaStepsParser}})
    // enEULASteps: any[];
    // @json({jsonName: 'maxoptra.ru.eula', mapper: {from: eulaStepsParser}})
    // ruEULASteps: any[];
    // @json({jsonName: 'maxoptra.es.eula', mapper: {from: eulaStepsParser}})
    // esEULASteps: any[];
    // @json({jsonName: 'maxoptra.pt.eula', mapper: {from: eulaStepsParser}})
    // ptEULASteps: any[];
    // @json({
    //     name: 'eula.additional.mandatory.approval',
    //     parser: (data: any): EulaCheckboxModel[] => {
    //         if (!data || !data.length || data.indexOf('{') == -1) return [];
    //         try {
    //             return EulaCheckboxModel.fromArray(JSON.parse(data));
    //         } catch (e) {
    //             return [];
    //         }
    //     }
    // })
    // eulaCheckboxes: EulaCheckboxModel[];

    @json(JsonMappers.Boolean)
    useMobileClient: boolean;

    // tariff
    @json(JsonMappers.Boolean)
    enableAutoScheduling: boolean;
    @json(JsonMappers.Boolean)
    enableManualScheduling: boolean;
    @json({
        value: true,
        mapper: JsonMappers.Boolean
    })
    enableAutoResequencing: boolean;
    @json(JsonMappers.Boolean)
    enableResequenceScheduling: boolean; // тарифная
    @json(JsonMappers.Boolean)
    enableSwapScheduling: boolean;
    @json(JsonMappers.Boolean)
    enableChangingStartTimeScheduling: boolean;
    @json(JsonMappers.Boolean)
    enablePlanningScreen: boolean;

    // TT planFact
    @json(JsonMappers.Boolean)
    enablePlanFact: boolean; // вкл/откл возможности перехода на ТТ
    @json(JsonMappers.Boolean)
    stubPlanFact: boolean; // включение заглушки на ТТ
    @json(JsonMappers.Boolean)
    planFactShowOperationalEnvironment: boolean;
    @json(JsonMappers.Boolean)
    planFactShowVehicleTrackTrace: boolean;
    @json(JsonMappers.Boolean)
    planFactShowSearchOrders: boolean;

    @json({
        jsonName: 'paymentNotification',
        mapper: JsonMappers.Boolean
    })
    paymentNotification: boolean;
    @json({
        jsonName: 'paymentNotificationText'
    })
    paymentNotificationText: string;

    @json(JsonMappers.Boolean)
    enablePodSettings: boolean;
    @json(JsonMappers.Boolean)
    enableManifestSettings: boolean;
    @json(JsonMappers.Boolean)
    disableZenDeskWidget: boolean;

    /**
     * Website widget
     */
    @json({
        jsonName: 'maxoptra.notification.enableLocationWidget',
        mapper: JsonMappers.Boolean
    })
    enableLocationWidget: boolean;

    @json({
        mapper: JsonMappers.Boolean
    })
    useNewWidgetService: boolean;

    @json({
        jsonName: 'maxoptra.notification.enableRateDelivery',
        mapper: JsonMappers.Boolean
    })
    enableRateDelivery: boolean;

    @json()
    communicationsReportTab: boolean;

    @json({jsonName: 'enable_remove_schedule_by_default', mapper: JsonMappers.Boolean})
    enableRemoveScheduleByDefault: boolean;
    @json({jsonName: 'enable_remove_schedule_before_import', mapper: JsonMappers.Boolean})
    enableRemoveScheduleBeforeImport: boolean;
    @json({jsonName: 'enable_import_on_selected_date', mapper: JsonMappers.Boolean}) // FIXME убрать нахрен
    enableImportOnSelectedDate: boolean;
    @json({jsonName: 'mobile_workflow_enable', mapper: JsonMappers.Boolean})
    mobileWorkflowEnable: boolean;

    @json()
    defaultTimeZone: string;

    @json()
    timeZone: string;

    @json({
        jsonName: 'planfact.additionInfoTemplate',
        mapper: {from: (data: any): any[] => (data ? JSON.parse(data) : [])}
    })
    planfactAdditionInfoTemplate: any[];

    @json({jsonName: 'planfact.enableExportSchedule', mapper: JsonMappers.Boolean})
    planfactEnableExportSchedule: boolean;
    @json({jsonName: 'planfact.enablePrintSchedule', mapper: JsonMappers.Boolean})
    planfactEnablePrintSchedule: boolean;
    @json({jsonName: 'planfact.showAdditionInfo', mapper: JsonMappers.Boolean})
    planfactShowAdditionInfo: boolean;

    @json({jsonName: 'break_type'})
    breakType: string;

    @json()
    tutorialUrlDemo: string;
    @json()
    tutorialUrlTrial: string;
    @json()
    whatsnewUrl: string; // Не приходит !!!

    @json({
        jsonName: 'default_customer_location_time_available'
    })
    defaultCustomerLocationTimeAvailable: string;
    @json()
    longDistanceUnits: string;
    @json()
    accountCurrency: string; // ISO код валюты
    @json({jsonName: 'capacity.units', value: 'kg'})
    weightUnits: string;
    @json({jsonName: 'capacity.label', value: 'weight'})
    weightLabel: string;
    @json()
    fuelRateUnits: string;
    @json()
    speedType: string;
    @json({jsonName: 'capacity.units', value: 'kg'})
    capacityUnits: string;
    @json({jsonName: 'capacity.decimalPrecision', value: 2})
    capacityDecimalPrecision: number;
    @json({
        value: 'm3'
    })
    volumeUnits: string;
    @json(JsonMappers.Boolean)
    vehicleTypes: boolean;
    @json(JsonMappers.Boolean)
    vehicleChecks: boolean;

    @json()
    useW3W: boolean;
    @json({
        value: 'volume'
    })
    volumeLabel: string;
    @json({jsonName: 'volume.decimalPrecision', value: 2})
    volumeDecimalPrecision: number;
    @json({jsonName: 'capacity.label', value: 'kg'})
    capacityLabel: string;
    @json(JsonMappers.Boolean)
    enableFuelPricesTab: boolean;
    @json(JsonMappers.Boolean)
    enableScanningTab: boolean;
    @json({jsonName: 'sms_notifications_enabled', mapper: JsonMappers.Boolean})
    smsNotificationsEnabled: boolean;

    @json({
        jsonName: 'email_notifications_enabled',
        mapper: JsonMappers.Boolean
    })
    emailNotificationsEnabled: boolean;

    @json()
    smsTags: any[];

    @json({jsonName: 'ptv.max_truck_height'})
    maxTruckHeight: number;
    @json({jsonName: 'ptv.max_truck_width'})
    maxTruckWidth: number;
    @json({jsonName: 'ptv.max_truck_length'})
    maxTruckLength: number;
    @json({jsonName: 'ptv.max_truck_weight'})
    maxTruckWeight: number;
    @json({jsonName: 'ptv.max_truck_axle_load'})
    maxTruckAxelLoad: number;

    @json({
        jsonName: 'sessionLifeTime',
        mapper: {from: (value: string): number => (value ? Number(value) : 30) * 60000}
    })
    sessionLifeTime: number;

    @json({
        jsonName: 'planFactFactRouteColor',
        value: '#099600'
    })
    planFactFactRouteColor: string;
    @json({
        jsonName: 'planFactPlannedRouteColor',
        value: '#2285ff'
    })
    planFactPlannedRouteColor: string;

    @json({
        jsonName: 'fixed_operation_duration',
        value: 0,
        mapper: {
            from: getFloat
        }
    })
    fixedOperationDuration: number;
    @json({
        jsonName: 'drop_duration_per_unit',
        value: 0,
        mapper: {
            from: getFloat
        }
    })
    dropDurationPerUnit: number;
    @json({
        jsonName: 'pickup_duration_per_unit',
        value: 0,
        mapper: {
            from: getFloat
        }
    })
    pickupDurationPerUnit: number;

    paymentNotificationHidden: boolean;

    @json(JsonMappers.Boolean)
    enableMultidayOptimisation: boolean;

    @json({jsonName: 'multiday_optimization_horizon'})
    multidayOptimizationHorizon: number;

    @json(JsonMappers.Boolean)
    enableIncrementalScheduling: boolean;

    @json({
        jsonName: 'enable_performer_role',
        mapper: JsonMappers.Boolean
    })
    enablePerformerRole: boolean;
    @json({
        jsonName: 'enable_dispatcher_role',
        mapper: JsonMappers.Boolean
    })
    enableDispatcheRole: boolean;
    @json({
        jsonName: 'enable_manager_role',
        mapper: JsonMappers.Boolean
    })
    enableManagerRole: boolean;
    @json({
        jsonName: 'enable_customer_service_role',
        mapper: JsonMappers.Boolean
    })
    enableCustomerServiceRole: boolean;
    @json({
        jsonName: 'enable_admin_role',
        mapper: JsonMappers.Boolean
    })
    enableAdminRole: boolean;

    @json(JsonMappers.Boolean)
    isRestrictedDispatcherSettings: boolean;

    @json({
        value: 'en'
    })
    currentLanguage: string;

    @json()
    locale: string;

    @json({jsonName: '2faEnabled', mapper: JsonMappers.Boolean})
    twoFactorAuthEnabled: boolean;

    @json({jsonName: '2faActive', mapper: JsonMappers.Boolean})
    twoFactorAuthActive: boolean;

    @json({
        jsonName: 'intercom.widget.app_id'
    })
    intercomWidgetAppId: string;

    @json({
        jsonName: 'ct.intercomWidget',
        mapper: JsonMappers.Boolean
    })
    useIntercomWidget: boolean;

    get defaultCustomerLocationTimeAvailableFrom(): string {
        const fromRegExp: RegExp = /^\d{2}\:\d{2}/;
        const execFrom: any = fromRegExp.exec(this.defaultCustomerLocationTimeAvailable);
        return execFrom ? execFrom[0] : '08:00';
    }

    get defaultCustomerLocationTimeAvailableTo(): string {
        const toRegExp: RegExp = /\d{2}\:\d{2}$/;
        const execTo: any = toRegExp.exec(this.defaultCustomerLocationTimeAvailable);
        return execTo ? execTo[0] : '17:00';
    }

    /**
     * @param holidays
     * @param dayjsFriendly - dayjs.js .day() отдает номер именно из массива ['sunday', ..., 'saturday']
     */
    getWeekitems(holidays: boolean = false, dayjsFriendly: boolean = false): string[] {
        let weekItems: string[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        if (!dayjsFriendly && this.dateFormats.weekstart == '1') {
            weekItems.push('sunday');
        } else {
            weekItems.unshift('sunday');
        }
        if (holidays) {
            weekItems.push('holiday');
        }
        return weekItems;
    }

    @json({
        from: (data: any): RoutingModeRecord[] => {
            let routingModesData: any[] = data ? JSON.parse(data) : [];
            return routingModesData.map((m: any) => fromJson(RoutingModeRecord, m));
        }
    })
    routingModes: RoutingModeRecord[];

    @json()
    currencyPosition: any;

    canShowPaymentNotification(): boolean {
        return !this.paymentNotificationHidden && this.paymentNotification;
    }

    hidePaymentNotification(): void {
        this.paymentNotificationHidden = true;
    }

    // стоимость одного часа работы
    getDefaultCostPerHour(): number {
        switch (this.accountCurrency) {
            case 'EUR':
            case 'GBP':
                return 10;
            case 'RUB':
                return 200;
            default:
                break;
        }
    }

    // стоимость за милю
    getDefaultCostPerMile(): number {
        switch (this.accountCurrency) {
            case 'EUR':
            case 'GBP':
                return 0.5;
            case 'RUB':
                return 15;
            default:
                break;
        }
    }

    getCurrencyCode(): string {
        return this.accountCurrency;
    }

    getCurrencyShowFormat(): ICurrencyShowFormat {
        let result: any;
        switch (this.getCurrencyCode()) {
            case 'RUB':
                result = {
                    ts: ' ',
                    ds: ',',
                    dl: 2
                };
                break;
            case 'JPY':
                result = {
                    ts: ',',
                    ds: '.',
                    dl: 3
                };
                break;
            default:
                result = {
                    ts: ',',
                    ds: '.',
                    dl: 2
                };
                break;
        }
        return {
            ...result,
            sign: this.getCurrencySign(),
            formatFunc: formatCurrencyFunc
        };
    }

    renderPrice: Function = (v: number): string => {
        if (this.currencyPosition == 'BEFORE') {
            if (v == v * 1) {
                return this.getCurrencySign() + ' ' + parseFloat(v + '').toFixed(2);
            } else {
                return this.getCurrencySign() + ' 0.00';
            }
        }
        if (this.currencyPosition == 'AFTER') {
            if (v == v * 1) {
                return parseFloat(v + '').toFixed(2) + ' ' + this.getCurrencySign();
            } else {
                return '0.00 ' + this.getCurrencySign();
            }
        }
    };

    getCurrencySign(): string {
        return this.lang[this.getCurrencyCode()];
    }

    renderLongDistanceUnits(v: string): string {
        return v + ' ' + this.getLongDistanceUnits();
    }

    getLongDistanceUnits(): string {
        return this.lang[this.longDistanceUnits];
    }

    getServerDate(epoch: number): Dayjs {
        return dayjs.utc(new Date(epoch + this.getTimezoneOffset(epoch)));
    }

    getTimezoneOffset(epoch: number): number {
        if (epoch || epoch == 0) {
            return new Date(epoch).getTimezoneOffset() * 60000;
        }
        return this.getBrowserDateShift();
    }

    getBrowserDateShift(): number {
        return new Date().getTimezoneOffset() * 60000;
    }

    getClientDateInDepotTimeZone(withoutTimeForDefault: boolean): Dayjs {
        throw 'Not supported';
        // let nowDate: Date = new Date();
        // if (withoutTimeForDefault) {
        //     nowDate.setHours(0);
        //     nowDate.setMinutes(0);
        // }
        // let currentUserTimezone: number = app.userModel && app.userModel.timezoneOffset;
        // return currentUserTimezone ? this.getServerDate(Date.now() + currentUserTimezone) : dayjs(nowDate);
    }

    // update(properties: {name: string; value: any}[]): void {
    //     this.__eachField((fieldName: string) => {
    //         const founded: any = properties.find((prop: any) => prop.name === fieldName);
    //         if (founded) {
    //             console.log('set', founded, founded.value);
    //             this[fieldName] = founded.value;
    //         }
    //     });
    //     // const fields: string[] = Object.keys(this.fields);
    //     // properties.forEach((property: {name: string; value: any}) => {
    //     //     const founded: string = fields.find(
    //     //         (key: string) => key === property.name || this.fields[key].name === property.name
    //     //     );
    //     //     if (founded) {
    //     //         console.log('set', founded, property.value);
    //     //         this[founded] = property.value;
    //     //     }
    //     // });
    // }
}
export const formatCurrencyFunc: any = (value: number, format: ICurrencyShowFormat, sign: boolean = true): string => {
    const splitted: string[] = ((value || 0) + '').split('.');
    const left: string = splitted[0];
    const right: string = splitted[1] || '';

    const arrLeft: string[] = left.split('');
    const arrRight: string[] = right.split('');

    const arrLeftResult: string[] = [];
    const arrRightResult: string[] = Array.from<string>({length: format.dl}).fill('0');

    while (arrLeft.length) {
        arrLeftResult.unshift(arrLeft.splice(-3).join(''));
    }

    arrRightResult.forEach((item: string, index: number, array: string[]) => {
        if (arrRight[index]) {
            array[index] = arrRight[index];
        }
    });

    const result: string = arrLeftResult.join(format.ts) + `${format.ds}${arrRightResult.join('')}`;

    return sign ? `${format.sign} ${result}` : result;
};
export interface ICurrencyShowFormat {
    formatFunc: Function;
    sign: string;
    ts: string; // thousands separator
    ds: string; // decimal part separator (radix)
    dl: number; // decimal length
}
